export interface ErrorContext extends Record<string, unknown> {
  /** Same as `cause` on the Error built-in */
  cause?: unknown;
}

const isLocalDevelopment = import.meta.env.DEV;
const isUnitTest = import.meta.env.MODE === 'test';

export default class ErrorWithContext extends Error {
  readonly context: Record<string, string>;

  constructor(message: string, context: ErrorContext) {
    const { cause, ...rest } = context;
    super(makeMessage(message, rest), { cause });
    this.context = { ...serializeContext(rest) };
  }
}

function makeMessage(message: string, context: Record<string, unknown>) {
  // Put the context in the error message for local dev and unit tests because
  // otherwise you can't see it
  if (isLocalDevelopment || isUnitTest) {
    return `${message} ${JSON.stringify(context)}`;
  }

  return message;
}

export function isErrorWithContext(err: unknown): err is ErrorWithContext {
  return err instanceof ErrorWithContext;
}

export function serializeContext(context: Record<string, unknown> | undefined): Record<string, string> {
  const serialized: Record<string, string> = {};

  for (const key in context) {
    if (Object.hasOwn(context, key)) {
      const value = context[key];
      const serializedValue = JSON.stringify(value, null, 2);

      // In the context object, all keys are allowed except for `type`.
      // https://docs.sentry.io/platforms/javascript/enriching-events/context/#structured-context
      const serializeKey = /_*type/.test(key) ? `_${key}` : key;
      serialized[serializeKey] = serializedValue;
    }
  }

  return serialized;
}
